import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { fadeTransition, FadeVariant, fadeVariants } from '@pkg/motion';
import {
    CommonAction,
    floorGameDuration as duration,
    floorGameEasing as easing,
    recipesCommonAction,
    styledMedia,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './SweepTicker.tokens';

export const StyledSweepTicker = styled.div`
    ${styledReset.borderBox};
    display: grid;
    align-content: end;
    justify-items: center;
    gap: ${tokens.ticker.gap}px;
    position: fixed;
    bottom: ${tokens.ticker.bottom}px;
    left: ${tokens.ticker.padding}px;
    right: ${tokens.ticker.padding}px;
    height: 0;
`;

export const CloseActionWrapper = styled(motion.div).attrs({
    variants: fadeVariants,
    initial: FadeVariant.Hidden,
    animate: FadeVariant.Show,
    exit: FadeVariant.Hidden,
    transition: fadeTransition,
})``;

export const CloseAction = styled(CommonAction)`
    ${recipesCommonAction.blurDark};
    padding: 0 ${tokens.closeAction.padding}px;
    height: ${tokens.closeAction.height}px;
    box-shadow: 0 2px 6px rgb(0, 0, 0, 0.4);
`;

export const CloseActionLabel = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    font-size: ${tokens.closeAction.labelSize}px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

export const Widget = styled.div<{ $open?: boolean }>`
    display: grid;
    grid-template-columns: auto 1fr;
    justify-self: start;
    overflow: hidden;
    width: ${tokens.openAction.size + 4}px;
    height: ${tokens.openAction.size + 4}px;
    border-radius: 999px;
    border: 2px solid transparent;
    background-color: rgb(255, 255, 255, 0.1);
    box-shadow: 0 2px 8px rgb(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
    transition-property: width, border-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    ${({ $open }) =>
        $open &&
        css`
            width: 100%;
            border-color: rgb(255, 255, 255, 0.2);
        `};
`;

export const OpenActionWrapper = styled.div`
    position: relative;
    z-index: 2;
`;

export const OpenAction = styled(CommonAction)`
    ${recipesCommonAction.basic};
    width: ${tokens.openAction.size}px;
    height: ${tokens.openAction.size}px;
    background-color: transparent;

    &:hover:not(:active) {
        background-color: rgb(255, 255, 255, 0.1);
    }
`;

export const IconWrapper = styled.div`
    ${styledMedia.iconWrapper};
    width: ${tokens.openAction.iconSize}px;
    height: ${tokens.openAction.iconSize}px;
`;

export const SweepControls = styled(motion.div).attrs({
    variants: fadeVariants,
    initial: FadeVariant.Hidden,
    animate: FadeVariant.Show,
    exit: FadeVariant.Hidden,
    transition: fadeTransition,
})`
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: min-content min-content;
    align-items: center;
    justify-content: space-between;
    gap: ${tokens.sweepControls.gap}px;
    padding-left: ${tokens.sweepControls.gap}px;
    padding-right: ${tokens.sweepControls.gap / 2}px;
`;

export const SweepAction = styled(CommonAction)`
    ${recipesCommonAction.primary};
    gap: ${tokens.sweepAction.gap}px;
    padding: 0 ${tokens.sweepAction.padding}px;
    height: ${tokens.sweepAction.height}px;
    white-space: nowrap;

    &:disabled {
        opacity: 0.8;
    }
`;

export const SweepActionLabel = styled.span`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    display: block;
    font-size: ${tokens.sweepAction.labelSize}px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
`;

export const SweepActionCurrency = styled.span`
    display: grid;
    place-items: center;
    place-content: center;
    width: ${tokens.sweepCurrency.size}px;
    height: ${tokens.sweepCurrency.size}px;
    border-radius: 999px;
    background-color: rgb(255, 255, 255, 0.1);
`;

export const CurrencyIconWrapper = styled.span`
    ${styledMedia.iconWrapper};
    width: ${tokens.sweepCurrency.iconSize}px;
    height: ${tokens.sweepCurrency.iconSize}px;
`;
